// Adapted from eval/dashboard/internal_web/src/components/session/beliefChange.ts (ATM-57): keyed on disease_name, no ground truth. Keep in step until ATM-60 shares it.
/**
 * What one answer did to the differential: each entry's move against the previous stored
 * belief, and the lines the slope chart draws between the two rankings. The eval dashboard's
 * `beliefChange`, keyed on the disease name (the graph variant med-reasoning ranked) and without
 * its ground-truth layer, which a live session has no truth for. Neither direction is scored — a
 * disease moving down can be exactly what a good answer should do — so nothing here says better
 * or worse.
 */

import type { DiseaseSnapshot } from "../../api/types";
import { signOf } from "../../lib/format";

/**
 * Each disease's first entry, in rank order. The graph can in principle list a name twice; the
 * first wins everywhere, so the rows, the lines and the changes agree about which one is drawn.
 */
export function firstByName(
  entries: readonly DiseaseSnapshot[],
): ReadonlyMap<string, DiseaseSnapshot> {
  const first = new Map<string, DiseaseSnapshot>();
  for (const entry of entries) if (!first.has(entry.disease_name)) first.set(entry.disease_name, entry);
  return first;
}

/** One entry's change since the previous belief; `rankDelta` > 0 means it moved up. */
export type EntryChange =
  | { kind: "new" }
  | { kind: "moved"; rankDelta: number; likelihoodDelta: number };

/** Every current entry's change, by disease name. */
export function entryChanges(
  current: readonly DiseaseSnapshot[],
  previous: readonly DiseaseSnapshot[],
): ReadonlyMap<string, EntryChange> {
  const before = firstByName(previous);
  const changes = new Map<string, EntryChange>();
  for (const [name, entry] of firstByName(current)) {
    const was = before.get(name);
    changes.set(
      name,
      was === undefined
        ? { kind: "new" }
        : {
            kind: "moved",
            rankDelta: was.rank - entry.rank,
            likelihoodDelta: entry.likelihood - was.likelihood,
          },
    );
  }
  return changes;
}

/**
 * One disease's line across an answer. Slots `0 … depth-1` are ranks 1 … depth; slot `depth`
 * is "below the shown top", where a disease enters from or leaves to.
 */
export type SlopeLine = {
  name: string;
  from: number;
  to: number;
  kind: "held" | "entered" | "left";
  /** For `entered` its rank before, for `left` its rank now; `null` when unranked there. */
  outsideRank: number | null;
};

/** Every line the slope chart draws between the previous belief's top and this one's. */
export function slopeLines(
  current: readonly DiseaseSnapshot[],
  previous: readonly DiseaseSnapshot[],
  depth: number,
): SlopeLine[] {
  const before = firstByName(previous);
  const now = firstByName(current);
  const shownNow = [...firstByName(current.slice(0, depth)).keys()];

  const kept: SlopeLine[] = shownNow.map((name, position) => {
    const rankBefore = before.get(name)?.rank;
    const inTopBefore = rankBefore !== undefined && rankBefore <= depth;
    return {
      name,
      from: inTopBefore ? rankBefore - 1 : depth,
      to: position,
      kind: inTopBefore ? "held" : "entered",
      outsideRank: inTopBefore ? null : (rankBefore ?? null),
    };
  });
  const left: SlopeLine[] = [...firstByName(previous.slice(0, depth)).values()]
    .filter((entry) => !shownNow.includes(entry.disease_name))
    .map((entry) => ({
      name: entry.disease_name,
      from: entry.rank - 1,
      to: depth,
      kind: "left",
      outsideRank: now.get(entry.disease_name)?.rank ?? null,
    }));
  return [...kept, ...left];
}

/** Below this a likelihood change is noise the panel does not print (0.1 percentage points). */
const MIN_PRINTED_CHANGE = 0.001;

/** A likelihood change in percentage points, "+12.3", or `null` when too small to print. */
export function pointsLabel(delta: number): string | null {
  if (Math.abs(delta) < MIN_PRINTED_CHANGE) return null;
  return `${signOf(delta)}${(Math.abs(delta) * 100).toFixed(1)}`;
}
